import AccountModel       from '@/models/services/account/AccountModel';
import UserModel          from '@models/account/UserModel';
import PagedCollection    from '@widesk-core/models/PagedCollection';
import { observable }     from 'mobx';
import { action }         from 'mobx';
import { runInAction }    from 'mobx';
import { makeObservable } from 'mobx';

export class AppStore {
	public readonly user = new UserModel();

	@observable
	private _account: AccountModel | null = null;

	@observable.ref
	private _accountMapUrns: Urn[] | null = null;

	@observable
	private _failedAccountId: id | null = null;

	constructor() {
		makeObservable(this);
		const localItem = localStorage.getItem('accountId');
		if (localItem) {
			this.fetchAccount(localItem);
		}
	}

	@action
	public async fetchAccount(id: id) {
		if (this._account?.id !== id) {
			this._account = new AccountModel({ id: id });
			this._accountMapUrns = null;

			try {
				await Promise.all([
					this._account.fetch(),
					this.refreshAccountMapUrns(),
				]);
			} catch (e) {
				runInAction(() => this._failedAccountId = id);
				this.resetAccount();
				return;
			}

			runInAction(() => this._failedAccountId = null);
			localStorage.setItem('accountId', id);
		}
	}

	public async refreshAccountMapUrns() {
		// import() dynamique : MapModel importe les stores de l'éditeur, un import statique
		// depuis appStore créerait un cycle à l'initialisation (TDZ @doc.model).
		const MapModel = (await import('@models/map/MapModel')).default;
		const collection = new PagedCollection(MapModel);

		try {
			await collection.setItemsPerPage(1000).setFilter('project.owner.accountUrn', this.accountUrn).list();
		} catch (e) {
			return;
		}

		runInAction(() => this._accountMapUrns = collection.models.map(map => map.urn));
	}

	@action
	public resetAccount() {
		this._account = null;
		this._accountMapUrns = null;
		localStorage.removeItem('accountId');
	}

	/** Urns de toutes les maps de l'account, `null` tant qu'ils ne sont pas chargés. */
	public get accountMapUrns() {
		return this._accountMapUrns;
	}

	public get failedAccountId() {
		return this._failedAccountId;
	}

	public get account() {
		return this._account;
	}

	public get accountUrn() {
		return this.account?.urn || (this.account?.id && AccountModel.makeUrn(this.account.id)) || '';
	}

	public clear() {
		this.user.clear();
		this.resetAccount();
		runInAction(() => this._failedAccountId = null);
	}
}

export default new AppStore();
